const LAYOUTS = {
  ONE_CARD: 0,
  TWO_CARDS: 1,
  THREE_CARDS: 2,
  FOUR_CARDS: 3,
};

if (!customElements.get("product-keyboard")) {
  customElements.define(
    "product-keyboard",
    class ProductKeyboard extends HTMLElement {
      connectedCallback() {
        this.selectionsClicked = {
          edition: false,
          pcb: false,
          plate: false,
          switches: false,
          keycaps: false,
          stabilizers: false,
        };
        this.selectedVariant = null;
        this._bindAll();
        this._queryRefs();
        this.renderStatus = Promise.resolve();
        // What each desktop card shows per step. "edition" items are the
        // keyboard's own images (see _showEditionMedia); "media" items are an
        // add-on product's gallery template; "helper" items are info panels.
        this.contentMap = {
          "product-title": {
            1: [{ type: "edition", angle: "all" }],
          },
          "select-edition": {
            1: [{ type: "edition", angle: "front" }],
            2: [{ type: "edition", angle: "side" }],
            3: [{ type: "edition", angle: "rear" }],
          },
          "select-pcb": {
            1: [{ type: "media", name: "pcb_images" }],
            2: [{ type: "helper", name: "pcb_helper" }],
          },
          "select-plate": {
            1: [{ type: "media", name: "plate_images" }],
            2: [{ type: "helper", name: "plate_helper" }],
          },
          "select-switches": {
            1: [{ type: "media", name: "switches_images" }],
            2: [{ type: "helper", name: "switches_helper" }],
          },
          "select-keycaps": {
            1: [{ type: "edition", angle: "combos" }],
            2: [{ type: "helper", name: "keycaps_helper_1" }],
            3: [{ type: "helper", name: "keycaps_helper_2" }],
            4: [{ type: "helper", name: "keycaps_helper_3" }],
          },
          "select-stabilizers": {
            1: [{ type: "media", name: "stabilizers_images" }],
            2: [{ type: "helper", name: "stabilizers_helper_1" }],
          },
          "product-summary": {
            1: [{ type: "edition", angle: "all" }],
          },
        };
        this._layoutMap = {
          "product-title": LAYOUTS.ONE_CARD,
          "select-edition": LAYOUTS.THREE_CARDS,
          "select-pcb": LAYOUTS.TWO_CARDS,
          "select-plate": LAYOUTS.TWO_CARDS,
          "select-switches": LAYOUTS.TWO_CARDS,
          "select-keycaps": LAYOUTS.FOUR_CARDS,
          "select-stabilizers": LAYOUTS.TWO_CARDS,
          "product-summary": LAYOUTS.ONE_CARD,
        };
        // Section setting "Single image for the edition step": the edition step
        // shows one large front image instead of front + side + bottom cards.
        if (this.dataset.singleImageView === "true") {
          this._layoutMap["select-edition"] = LAYOUTS.ONE_CARD;
          this.contentMap["select-edition"] = {
            1: [{ type: "edition", angle: "front" }],
          };
        }
        // Data-driven dispatch config for _renderMainSection.
        // skipLayoutTransition: update cards without a view transition
        // alwaysUpdateCards: always do layout + card update (even when re-selecting within same section)
        // variantLookup: property name on `this` for getIdByOptionValue mapping
        // Mobile galleries are refreshed by updateMobileGalleries after every render.
        this._selectionBehavior = {
          "product-title": { skipLayoutTransition: true },
          "select-edition": { alwaysUpdateCards: true },
          "select-pcb": { variantLookup: "pcbVariants" },
          "select-plate": { variantLookup: "plateVariants" },
          "select-switches": {},
          "select-keycaps": {},
          "select-stabilizers": {},
          "product-summary": { alwaysUpdateCards: true },
        };
        this._isRendering = false;
        this.currentSelectionId = "product-title";

        this._initScrollLayoutManager();
        if (!this.configSelects) return;

        // Listeners
        this.configSelects.addEventListener("change", this._onAnyChange);
        this._initCascadingForm();

        // Simple scroll-based selection summary visibility
        this._initSelectionSummaryScroll();

        // Summary view button
        this._initSummaryButton();

        // Variant preselect via URL
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has("summary")) {
          this._showSummaryView();
        } else {
          document.querySelector("footer").classList.add("hidden");
        }

        if (urlParams.has("variant")) {
          // Remove all cascade locks - enable all fieldsets
          this.querySelectorAll("[data-cascade-group]").forEach((group) => {
            group.removeAttribute("data-cascade");
            const fieldset = group.querySelector("fieldset");
            if (fieldset) fieldset.removeAttribute("disabled");
          });

          // Mark all selections clicked
          Object.keys(this.selectionsClicked).forEach(
            (key) => (this.selectionsClicked[key] = true)
          );

          const selNode = document.querySelector(
            "script[data-selected-variant]"
          );

          if (selNode?.textContent?.trim()) {
            try {
              this.selectedVariant = JSON.parse(selNode.textContent.trim());
            } catch (_) {}
          }

          // Enable submit
          if (this.selectedVariant?.available) {
            this.querySelector("[data-cascade-submit]")?.removeAttribute(
              "disabled"
            );
          }

          const editionOption = this.getSelectedOption("Edition");
          const pcbOption = this.getSelectedOption("PCB");
          const plateOption = this.getSelectedOption("Plate");
          this.updateKeyboardInput(urlParams.get("variant"));
          this.updateInput("select-edition", editionOption);
          this.updateInput("select-pcb", pcbOption);
          this.updateInput("select-plate", plateOption);
          this.updateInput("select-switches", urlParams.get("switches"));
          this.updateInput("select-keycaps", urlParams.get("keycaps"));
          this.updateInput("select-stabilizers", urlParams.get("stabilizers"));
          this.updateOptionValues(document);
          this.updateReviewValues(document);
          this.updateTotalPrice();
          this.updateEstimatedShipDate();
          this.updateReviewButton(document);
        }

        this.initMobileGalleries(document);
        // A keycap set preselected from the URL shows its combo from the start.
        if (urlParams.has("keycaps")) {
          this._updateCards("product-title");
          this.updateMobileGalleries(document);
        }
      }

      disconnectedCallback() {
        this.configSelects?.removeEventListener("change", this._onAnyChange);
        this._abortMain?.abort?.();
        this._teardownScrollLayoutManager();
        window.removeEventListener("scroll", this._onScroll);
        if (this._handlePopState) {
          window.removeEventListener("popstate", this._handlePopState);
        }
      }

      // ---------- Setup / refs ----------
      _bindAll() {
        this._onAnyChange = this._onAnyChange.bind(this);
        this.updateTotalPrice = this.updateTotalPrice.bind(this);
        this.updateEstimatedShipDate = this.updateEstimatedShipDate.bind(this);
        this.initMobileGalleries = this.initMobileGalleries.bind(this);
        this.updateReviewValues = this.updateReviewValues.bind(this);
        this._renderMainSection = this._renderMainSection.bind(this);
        this._onCascadeChange = this._onCascadeChange.bind(this);
        this._initSelectionSummaryScroll =
          this._initSelectionSummaryScroll.bind(this);
        this._onScroll = this._onScroll.bind(this);
      }

      _queryRefs() {
        this.configSelects = this.querySelector("config-selects");
        this.form = this.querySelector("product-form");
        this.productOptions = JSON.parse(
          this.querySelector("[data-product-options]").textContent.trim()
        );

        this.sectionId = this.dataset.section || "";
        this.productUrl = this.dataset.url || window.location.pathname || "";

        this.plateVariants = JSON.parse(
          this.querySelector("#plateVariantsData").textContent.trim()
        );
        this.pcbVariants = JSON.parse(
          this.querySelector("#pcbVariantsData").textContent.trim()
        );
        this.switchVariants = JSON.parse(
          this.querySelector("#switchVariantsData").textContent.trim()
        );
        this.keycapVariants = JSON.parse(
          this.querySelector("#keycapVariantsData").textContent.trim()
        );
        this.stabilizerVariants = JSON.parse(
          this.querySelector("#stabilizerVariantsData").textContent.trim()
        );
      }

      // ---------- Cascading Form Logic ----------
      _initCascadingForm() {
        this.cascadeGroups = [
          "edition",
          "pcb",
          "plate",
          "switches",
          "stabilizers",
          "keycaps",
          "complete",
        ];
        this.addEventListener("change", this._onCascadeChange);
        this._updateCascadeState();
      }

      _onCascadeChange(e) {
        const group = e.target.closest("[data-cascade-group]");
        if (!group) return;
        if (e.target?.parentElement?.querySelector(".label-unavailable"))
          return;

        group.scrollIntoView({ behavior: "smooth", block: "start" });
        this.selectionsClicked[group.dataset.cascadeGroup] = true;

        // Wait for any in-progress render to complete
        this.renderStatus.then(() => {
          if (group.dataset.cascadeGroup === "edition") {
            this._toggleSelectionSummary(true);
          }

          if (
            e.target.type === "radio" &&
            e.target.checked &&
            group.hasAttribute("data-cascade")
          ) {
            this._updateCascadeState();
            group.removeAttribute("data-cascade");
          }
        });
      }

      _updateCascadeState() {
        let allFieldsSelected = true;

        for (let i = 0; i < this.cascadeGroups.length; i++) {
          const groupName = this.cascadeGroups[i];
          const groupElement = this.querySelector(
            `[data-cascade-group="${groupName}"]`
          );
          const fieldset = groupElement?.querySelector("fieldset");
          if (!groupElement || !fieldset) continue;

          const shouldEnable =
            i === 0 || this._hasSelection(this.cascadeGroups[i - 1]);
          if (shouldEnable) fieldset.removeAttribute("disabled");
          else {
            fieldset.setAttribute("disabled", "");
            this._clearFieldsetSelection(fieldset);
          }
          if (!this._hasSelection(groupName)) allFieldsSelected = false;
        }

        const submitButton = this.querySelector("[data-cascade-submit]");
        if (submitButton) {
          if (allFieldsSelected) submitButton.removeAttribute("disabled");
          else submitButton.setAttribute("disabled", "");
        }
      }

      _hasSelection(groupName) {
        if (groupName === "complete") return true;
        const groupElement = this.querySelector(
          `[data-cascade-group="${groupName}"]`
        );
        if (!groupElement) return false;
        return !!groupElement.querySelector('input[type="radio"]:checked');
      }

      _clearFieldsetSelection(fieldset) {
        fieldset
          .querySelectorAll('input[type="radio"]')
          .forEach((input) => (input.checked = false));
      }

      // ---------- Event handlers ----------
      _onAnyChange(e) {
        try {
          this._renderMainSection(e);
        } catch (err) {
          console.error("Error in _onAnyChange", err);
        }
      }


      // ---------- Section Rendering ----------
      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length)
          params.push(`option_values=${optionValues.join(",")}`);
        return `${url}?${params.join("&")}`;
      }

      get selectedOptionValues() {
        return Array.from(
          this.configSelects.querySelectorAll(
            "select option[selected], fieldset input:checked"
          )
        ).map(({ dataset }) => dataset.optionValueId);
      }

      updateOptionValues(html = document) {
        const editionSummaryEl = this.querySelector("#select-edition-summary");
        const pcbSummaryEl = this.querySelector("#select-pcb-summary");
        const plateSummaryEl = this.querySelector("#select-plate-summary");
        const switchesSummaryEl = this.querySelector(
          "#select-switches-summary"
        );
        const keycapsSummaryEl = this.querySelector("#select-keycaps-summary");
        const stabilizersSummaryEl = this.querySelector(
          "#select-stabilizers-summary"
        );

        if (this.selectionsClicked.edition) {
          editionSummaryEl.classList.add("text-foreground");
          editionSummaryEl.classList.add("selected");
        }

        if (this.selectionsClicked.pcb) {
          pcbSummaryEl.classList.add("text-foreground");
          pcbSummaryEl.classList.add("selected");
        }

        if (this.selectionsClicked.plate) {
          plateSummaryEl.classList.add("text-foreground");
          plateSummaryEl.classList.add("selected");
        }

        if (this.selectionsClicked.switches) {
          switchesSummaryEl.classList.add("text-foreground");
          switchesSummaryEl.classList.add("selected");
        }

        if (this.selectionsClicked.keycaps) {
          keycapsSummaryEl.classList.add("text-foreground");
          keycapsSummaryEl.classList.add("selected");
        }

        if (this.selectionsClicked.stabilizers) {
          stabilizersSummaryEl.classList.add("text-foreground");
          stabilizersSummaryEl.classList.add("selected");
        }

        const container = document.querySelector("[data-progress]");
        const selectedCount = document.querySelectorAll(".selected").length;
        container.setAttribute("data-progress", selectedCount);
      }

      updateHelperInputs(html = document) {
        // Prefer input:checked (live DOM state) over [data-checked] (static server attribute).
        // When html is a parsed fetch response, input:checked won't match (no checked attr),
        // so [data-checked] serves as the fallback for server-rendered selections.
        const selectedPcbEl = html.querySelector("#select-pcb input:checked")
          || html.querySelector("#select-pcb [data-checked]");
        const pcbHelperInputs = document.querySelectorAll("[data-pcb-helper]");
        if (selectedPcbEl) {
          pcbHelperInputs.forEach((helperInput) => {
            helperInput.checked = false;
            const selectedOption = selectedPcbEl.value.toLowerCase();
            const helperInputOption = helperInput.dataset.option.toLowerCase();
            helperInput.checked = selectedOption.includes(helperInputOption);
          });
        }

        const selectedPlateEl = html.querySelector("#select-plate input:checked")
          || html.querySelector("#select-plate [data-checked]");
        const plateHelperInputs = document.querySelectorAll(
          "[data-plate-helper]"
        );

        if (selectedPlateEl) {
          plateHelperInputs.forEach((helperInput) => {
            const selectedOption = selectedPlateEl.value.toLowerCase();
            const helperInputOption = helperInput.dataset.option.toLowerCase();
            helperInput.setAttribute(
              "data-selected",
              selectedOption.includes(helperInputOption)
            );
          });
        }

        const selectedSwitchesEl = document.querySelector(
          "#select-switches input:checked"
        );
        const selectSwitchesId = selectedSwitchesEl?.value;
        const switchesHelperInputs = document.querySelectorAll(
          "[data-switches-helper]"
        );
        const switchVariant = this.switchVariants.find((v) => {
          return v.id == selectSwitchesId;
        });
        if (switchVariant) {
          switchesHelperInputs.forEach((helperInput) => {
            helperInput.checked = false;
            const selectedOption = `${switchVariant.id}`;
            const helperInputOption = `${helperInput.dataset.option}`;
            helperInput.checked = selectedOption === helperInputOption;
          });
        }
      }

      initMobileGalleries(html = document) {
        const containers = this.querySelectorAll("div[data-mobile-gallery]");

        containers.forEach((container) => {
          const name = container.getAttribute("data-mobile-gallery");
          if (!name) return;

          const esc =
            window.CSS && CSS.escape
              ? CSS.escape(name)
              : name.replace(/"/g, '\\"');
          const sourceTpl = html.querySelector(`template[data-name="${esc}"]`);
          if (!sourceTpl) return;

          const frag = sourceTpl.content.cloneNode(true);
          container.replaceChildren(frag);
        });
      }

      /**
       * Refresh the mobile edition galleries (data-mobile-gallery="edition_*")
       * from a freshly rendered section so they follow the edition and the
       * selected keycaps. Add-on mobile galleries never change per edition.
       * @param {Document} html - the rendered section to read templates from
       */
      updateMobileGalleries(html = document) {
        this.querySelectorAll('[data-mobile-gallery^="edition_"]').forEach(
          (container) => {
            const angle = container.dataset.mobileGallery.slice(
              "edition_".length
            );
            const gallery = container.querySelector("media-gallery");
            if (gallery) this._showEditionMedia(gallery, angle, html);
          }
        );
      }

      getSelectedOption(name) {
        const selectedOptions = this.selectedVariant?.options;
        const productOption = this.productOptions.find(
          (option) => option.name === name
        );
        const selectedOption = selectedOptions.find((option) =>
          productOption.values.includes(option)
        );
        return selectedOption;
      }

      updateReviewValues(html) {
        const selectedEdition = this.getSelectedOption("Edition");
        const selectedPcb = this.getSelectedOption("PCB");
        const selectedPlate = this.getSelectedOption("Plate");

        const selectedSwitchesEl = this.querySelector(
          "#select-switches input:checked"
        )?.parentElement?.querySelector("[data-title]");
        const selectedKeycapsEl = this.querySelector(
          "#select-keycaps input:checked"
        )?.parentElement?.querySelector("[data-title]");
        const selectedStabilizersEl = this.querySelector(
          "#select-stabilizers input:checked"
        )?.parentElement?.querySelector("[data-title]");

        const editionReviewEl = this.querySelector("#edition-review");
        const pcbReviewEl = this.querySelector("#pcb-review");
        const plateReviewEl = this.querySelector("#plate-review");
        const switchesReviewEl = this.querySelector("#switches-review");
        const keycapsReviewEl = this.querySelector("#keycaps-review");
        const stabilizersReviewEl = this.querySelector("#stabilizers-review");

        editionReviewEl.innerHTML = selectedEdition || "No edition";
        pcbReviewEl.innerHTML = selectedPcb || "No pcb";
        plateReviewEl.innerHTML = selectedPlate || "No plate";
        switchesReviewEl.innerHTML =
          selectedSwitchesEl?.innerHTML || "No switches";
        keycapsReviewEl.innerHTML =
          selectedKeycapsEl?.innerHTML || "No keycaps";
        stabilizersReviewEl.innerHTML =
          selectedStabilizersEl?.innerHTML || "No stabilizers";
      }

      updateReviewButton(doc = document) {
        let allFieldsSelected = true;

        for (let i = 0; i < this.cascadeGroups.length; i++) {
          const groupName = this.cascadeGroups[i];
          if (!this._hasSelection(groupName)) allFieldsSelected = false;
        }
        const reviewButton = document.getElementById(
          `ProductReviewButton-${this.sectionId}`
        );

        const selectedSwitchesEl = document.querySelector(
          "#select-switches input:checked"
        );
        const selectSwitchesId = selectedSwitchesEl?.value;
        const switchVariant = this.switchVariants.find((v) => {
          return v.id == selectSwitchesId;
        });

        const selectedKeycapsEl = document.querySelector(
          "#select-keycaps input:checked"
        );
        const selectKeycapsId = selectedKeycapsEl?.value;
        const keycapVariant = this.keycapVariants.find((v) => {
          return v.id == selectKeycapsId;
        });

        const selectedStabilizersEl = document.querySelector(
          "#select-stabilizers input:checked"
        );
        const selectStabilizersId = selectedStabilizersEl?.value;
        const stabilizerVariant = this.stabilizerVariants.find((v) => {
          return v.id == selectStabilizersId;
        });

        const available =
          this.selectedVariant?.available &&
          (switchVariant ? switchVariant.available : true) &&
          (keycapVariant ? keycapVariant.available : true) &&
          (stabilizerVariant ? stabilizerVariant.available : true);
        const disable = !available || !allFieldsSelected;
        let newButtonText = doc.querySelector(
          "[data-js-product-add-to-cart-text]"
        ).textContent;
        if (!available) {
          newButtonText = "Sold Out";
        }

        if (disable) {
          reviewButton.disabled = true;
          reviewButton.setAttribute("aria-disabled", "true");
          reviewButton.querySelector(
            "[data-js-product-add-to-cart-text]"
          ).textContent = newButtonText;
        } else {
          reviewButton.disabled = false;
          reviewButton.removeAttribute("aria-disabled");
          reviewButton.querySelector(
            "[data-js-product-add-to-cart-text]"
          ).textContent = newButtonText;
        }
      }

      updateKeyboardInput(variantId) {
        const input = this.querySelector('[data-role="item-keyboard-id"]');
        if (input) {
          input.value = variantId;
          input.checked = true;
        }
      }

      updateInput(inputId, value) {
        if (!value) return;
        const fieldset = this.querySelector(`#${inputId} fieldset`);

        fieldset.querySelectorAll("input").forEach((input) => {
          input.checked = input.value === value;
        });
      }

      updateURL(url, variantId, switchesId, keycapsId, stabilizersId) {
        if (this.dataset.updateUrl !== "false") {
          window.history.replaceState(
            {},
            "",
            `${url}${variantId ? `?variant=${variantId}` : ""}${
              switchesId ? `&switches=${switchesId}` : ""
            }${keycapsId ? `&keycaps=${keycapsId}` : ""}${
              stabilizersId ? `&stabilizers=${stabilizersId}` : ""
            }`
          );
        }
      }

      updateTotalPrice() {
        const priceTexts = this.querySelectorAll("[data-js-total-price]");

        priceTexts.forEach((priceText) => {
          const newPrice = this.form?.getTotalPrice();
          if (priceText && newPrice != null) priceText.innerHTML = newPrice;
        });
      }

      updateEstimatedShipDate() {
        const globalEstimatedShipDate = this.form?.getEstimatedShipDate();
        const wrappers = this.querySelectorAll("[data-estimated-ship-wrapper]");

        wrappers.forEach((wrapper) => {
          const span = wrapper.querySelector("[data-estimated-ship-date]");
          const useVisibility = wrapper.classList.contains("invisible") || wrapper.classList.contains("visible");
          const inSummary = wrapper.closest("#selection-summary");

          let displayDate = null;

          const inBuildRecap = wrapper.closest("#keyboard-summary");

          if (inSummary || inBuildRecap) {
            // Summary bar & build recap: show the global max ship date, fall back to product date or shop lead time
            displayDate = globalEstimatedShipDate;
            if (displayDate == null) {
              const summaryEl = inSummary || this.querySelector("#selection-summary");
              const productShipDate = summaryEl?.dataset?.productShipDate;
              if (productShipDate) {
                displayDate = productShipDate;
              } else if (window.shopShippingTime) {
                displayDate = `within ${window.shopShippingTime} days`;
              }
            }
          } else {
            // Section-specific: find the checked input within this section and show its ship date
            const section = wrapper.closest("[data-cascade-group]");
            if (section) {
              const groupName = section.dataset.cascadeGroup;
              if (groupName === "edition") {
                // Edition uses the selected keyboard variant's ship date
                if (this.selectedVariant) {
                  const shipDate = this.form?.getVariantEstimatedShipDate(this.selectedVariant.id);
                  displayDate = this.form?.formatEstimatedShipDate(shipDate);
                }
              } else {
                // Add-on sections (switches, keycaps, stabilizers): use the checked radio's variant ID
                const checkedInput = section.querySelector("input[type='radio']:checked");
                if (checkedInput && checkedInput.value) {
                  const shipDate = this.form?.getVariantEstimatedShipDate(checkedInput.value);
                  displayDate = this.form?.formatEstimatedShipDate(shipDate);
                }
              }
            }
          }

          if (displayDate != null) {
            if (span) span.innerHTML = displayDate;
            if (useVisibility) {
              wrapper.classList.remove("invisible");
              wrapper.classList.add("visible");
            } else {
              wrapper.classList.remove("hidden");
              wrapper.classList.add("flex");
            }
          } else {
            if (span) span.innerHTML = "";
            if (useVisibility) {
              wrapper.classList.add("invisible");
              wrapper.classList.remove("visible");
            } else {
              wrapper.classList.add("hidden");
              wrapper.classList.remove("flex");
            }
          }
        });
      }

      resetProductFormState() {
        try {
          this.form?.toggleSubmitButton(true);
          this.form?.handleErrorMessage();
        } catch (err) {
          console.error("Error in resetProductFormState", err);
        }
      }

      // ================================
      // Edition media
      // ================================
      // The keyboard's own images come from the variant_media metaobjects and
      // are rendered by Liquid into template[data-name="edition_<angle>"]:
      // "all" (iso, front, side, rear with dots), one template per angle, and
      // "combos" (one front slide per keycap set, tagged data-keycap-sku).
      // Everything that shows the keyboard (desktop cards, mobile galleries,
      // the summary) asks _showEditionMedia for an angle and gets the same
      // answer: the selected keycap combo when there is one, else the angle.

      /**
       * The slide container + index for the selected keycap combo, or null
       * when no keycap is selected or this edition has no slide for it.
       * @param {Document} doc - document holding the edition templates
       */
      _selectedKeycapSlide(doc = document) {
        const selectedKeycapId = this.querySelector(
          "#select-keycaps input:checked"
        )?.value;
        if (!selectedKeycapId) return null;

        const container = this._editionTemplate("combos", doc);
        const sku = this.keycapVariants
          ?.find((v) => `${v.id}` == selectedKeycapId)
          ?.sku?.toLowerCase();
        const item =
          sku && container?.querySelector(`[data-keycap-sku="${sku}"]`);
        const index = item ? parseInt(item.dataset.index, 10) : NaN;
        return isNaN(index) ? null : { container, index };
      }

      _editionTemplate(angle, doc = document) {
        const tpl = doc.querySelector(`template[data-name="edition_${angle}"]`);
        return tpl?.content.querySelector("[data-container]") || null;
      }

      /**
       * Fill `gallery` with the edition slides for `angle` ("all", "front",
       * "side", "rear" or "combos"). Front-facing angles and the combo strip
       * show the selected keycap combo when one is chosen; side and rear
       * always show the edition's default angle. The slide set and the start
       * index go to setImages together so no earlier jump can block this one.
       * @returns {boolean} true when the gallery was filled
       */
      _showEditionMedia(gallery, angle, doc = document) {
        if (!gallery) return false;
        const showsKeycaps =
          angle === "all" || angle === "front" || angle === "combos";
        const slides = showsKeycaps ? this._selectedKeycapSlide(doc) : null;
        if (slides) {
          gallery.setImages(slides.container, slides.index);
          return true;
        }
        const container = this._editionTemplate(angle, doc);
        if (!container) return false;
        gallery.setImages(container);
        return true;
      }

      /**
       * Add-on galleries (PCB, plate, switches, stabilizers) are built from
       * those products' media, so the slide for the chosen variant is the one
       * carrying its featured media id. Edition galleries are metaobject
       * slides and are filled by _showEditionMedia instead.
       * @param {string} selectionId - the add-on step whose galleries to move
       * @param {string|number} variantId - the chosen add-on variant
       */
      _showAddonMedia(selectionId, variantId) {
        const dataMap = {
          "select-pcb": "pcb-variants",
          "select-plate": "plate-variants",
          "select-switches": "switch-variants",
          "select-stabilizers": "stabilizer-variants",
        };

        const dataAttr = dataMap[selectionId];
        if (!dataAttr) return;

        const script = this.querySelector(`[data-${dataAttr}]`);
        if (!script?.textContent) return;

        try {
          const variants = JSON.parse(script.textContent);
          const variant = variants.find((v) => `${v.id}` == variantId);
          const mediaId =
            variant?.featured_media?.id || variant?.product?.featured_media?.id;
          if (!mediaId) return;

          const compositeId = `${this.sectionId}-${mediaId}`;
          this.querySelectorAll(`[data-select='${selectionId}']`).forEach(
            (el) => {
              const gallery = el.setActiveMedia
                ? el
                : el.querySelector("media-gallery");
              gallery?.setActiveMedia(compositeId);
            }
          );
        } catch (e) {
          console.error(`Failed to parse ${dataAttr}:`, e);
        }
      }

      runKeycapsHydrator(doc) {
        const src = doc.getElementById("hydrate-keycaps-data");
        if (!src) return;

        const s = document.createElement("script");
        s.type = "application/javascript";
        s.text = src.textContent; // run exactly what the server sent
        document.head.appendChild(s); // executes on insert
        s.remove();
      }

      _renderMainSection(e = null) {
        if (!this.productUrl || !this.sectionId) return;

        this._abortMain?.abort?.();
        this._abortMain = new AbortController();

        const url = `${this.productUrl}?option_values=${encodeURIComponent(
          this.selectedOptionValues.join(",")
        )}&section_id=${encodeURIComponent(this.sectionId)}`;

        // Create a new promise that resolves when this render completes
        this._isRendering = true;
        this.renderStatus = fetch(url, { signal: this._abortMain.signal })
          .then((r) => r.text())
          .then((htmlText) => {
            const doc = new DOMParser().parseFromString(htmlText, "text/html");

            const selNode = doc.querySelector("script[data-selected-variant]");

            if (selNode?.textContent?.trim()) {
              this.selectedVariant = JSON.parse(selNode.textContent.trim());
              try {
                // Edition templates change with the variant; add-on templates do not.
                doc
                  .querySelectorAll('template[data-name^="edition_"]')
                  .forEach((newTpl) => {
                    const tpl = this.querySelector(
                      `template[data-name="${newTpl.dataset.name}"]`
                    );
                    if (tpl) tpl.innerHTML = newTpl.innerHTML;
                  });

                // Update CMF drawer body with fresh edition data
                const cmfDrawer = this.querySelector('[data-cmf-drawer]');
                const newCmfDrawer = doc.querySelector('[data-cmf-drawer]');
                if (cmfDrawer && newCmfDrawer) {
                  cmfDrawer.updateBody(newCmfDrawer.innerHTML);
                }
              } catch (_) {}
            }

            this.runKeycapsHydrator(doc);

            const switchesId = this.querySelector(
              "#select-switches input:checked"
            )?.value;
            const keycapsId = this.querySelector(
              "#select-keycaps input:checked"
            )?.value;
            const stabilizersId = this.querySelector(
              "#select-stabilizers input:checked"
            )?.value;

            this.updateKeyboardInput(this.selectedVariant?.id);
            this.updateOptionValues(doc);
            this.updateURL(
              this.productUrl,
              this.selectedVariant?.id,
              switchesId,
              keycapsId,
              stabilizersId
            );

            if (e) {
              const selectionId = e.target.closest("[data-cascade-group]")?.id;
              const behavior = this._selectionBehavior[selectionId];
              if (behavior) {
                const isCurrentSection =
                  this.currentSelectionId === selectionId;

                if (behavior.skipLayoutTransition) {
                  // product-title: update cards without layout transition
                  this._updateCards(selectionId, doc);
                } else if (
                  behavior.alwaysUpdateCards ||
                  !isCurrentSection
                ) {
                  // First visit or always-update: layout transition + card update
                  const nextLayout = this._layoutMap[selectionId];
                  this._vtManager.setLayout(nextLayout, () => {
                    this._updateCards(selectionId, doc);
                  });
                } else {
                  // Already viewing this section: just update gallery
                  const selectedInput = e.target.closest(
                    `#${selectionId} input:checked`
                  );
                  let variantId;
                  if (behavior.variantLookup) {
                    variantId = this.getIdByOptionValue(
                      selectedInput.value,
                      this[behavior.variantLookup]
                    );
                  } else {
                    variantId = selectedInput?.value;
                  }

                  if (selectionId === "select-keycaps") {
                    const card1Gallery = this.layoutRoot?.querySelector(
                      '[data-card="1"] media-gallery'
                    );
                    this._showEditionMedia(card1Gallery, "combos", doc);
                  } else {
                    this._showAddonMedia(selectionId, variantId);
                  }
                }
              }
            }

            if (selNode) {
              const localSel = this.querySelector(
                "script[data-selected-variant]"
              );
              if (localSel) localSel.textContent = selNode.textContent;
            }

            let allFieldsSelected = true;

            for (let i = 0; i < this.cascadeGroups.length; i++) {
              const groupName = this.cascadeGroups[i];
              if (!this._hasSelection(groupName)) allFieldsSelected = false;
            }

            const available = this.selectedVariant?.available;
            const disableSubmit = !available || !allFieldsSelected;
            this.form?.toggleSubmitButton(
              disableSubmit,
              window.variantStrings?.soldOut
            );

            requestAnimationFrame(() => {
              this.updateMobileGalleries(doc);
              this.updateReviewValues(doc);
              this.updateTotalPrice();
              this.updateEstimatedShipDate();
              this.updateReviewButton(doc);
              this.updateHelperInputs(doc);
            });

            try {
              publish?.(PUB_SUB_EVENTS.variantChange, {
                data: {
                  sectionId: this.sectionId,
                  html: doc,
                  variant: this.selectedVariant,
                },
              });
            } catch (_) {}
          })
          .catch((err) => {
            if (err?.name === "AbortError") return;
            console.error("Main section render failed", err);
          })
          .finally(() => {
            this._isRendering = false;
            this.currentSelectionId = e.target.closest(
              "[data-cascade-group]"
            )?.id;
          });
      }

      getIdByOptionValue(optionValue, variants) {
        const v = variants.find((v) =>
          v.options.some((o) => o === optionValue)
        );
        return v ? v.id : null;
      }

      // ================================
      // Selection Summary Visibility
      // ================================
      _initSelectionSummaryScroll() {
        const summary = this.querySelector("#selection-summary");
        if (!summary) return;
        window.addEventListener("scroll", this._onScroll);
      }

      _onScroll() {
        const shouldShow = window.scrollY > 20;
        this._toggleSelectionSummary(shouldShow);
      }

      _toggleSelectionSummary(shouldShow) {
        const summary = this.querySelector("#selection-summary");
        if (!summary) return;
        summary.classList.toggle("opacity-100", shouldShow);
        summary.classList.toggle("translate-y-0", shouldShow);
        summary.classList.toggle("md:opacity-0", !shouldShow);
        summary.classList.toggle("md:translate-y-full", !shouldShow);
      }

      // ================================
      // Summary View Toggle
      // ================================
      _initSummaryButton() {
        const showButton = this.querySelector("[data-show-summary]");
        if (showButton) {
          showButton.addEventListener("click", (e) => {
            e.preventDefault();
            this._showSummaryView();

            // Update URL with pushState for back button support
            const currentUrl = window.location.href;
            const separator = currentUrl.includes("?") ? "&" : "?";
            const newUrl = currentUrl + separator + "summary";

            window.history.pushState({ summary: true }, "", newUrl);
          });
        }

        const editButton = this.querySelector("[data-hide-summary]");
        if (editButton) {
          editButton.addEventListener("click", (e) => {
            e.preventDefault();
            this._hideSummaryView();

            // Remove summary param from URL
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete("summary");
            const newSearch = urlParams.toString();
            const newUrl =
              window.location.pathname + (newSearch ? "?" + newSearch : "");

            window.history.pushState({ summary: false }, "", newUrl);
          });
        }

        // Handle back button
        this._handlePopState = () => {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.has("summary")) {
            this._showSummaryView();
          } else {
            this._hideSummaryView();
          }
        };
        window.addEventListener("popstate", this._handlePopState);
      }

      _showSummaryView() {
        window.scrollTo(0, 0);
        document.querySelector("body")?.classList.remove("dark");

        const nextLayout = this._layoutMap["product-summary"];
        this._vtManager.setLayout(nextLayout, () => {
          this._updateCards("product-summary");
        });

        this.updateMobileGalleries(document);

        this.classList.add("show-summary");
        document.querySelector("footer")?.classList.remove("hidden");
      }

      _hideSummaryView() {
        document.querySelector("body")?.classList.add("dark");
        this.classList.remove("show-summary");
        document.querySelector("footer")?.classList.add("hidden");
      }

      // ================================
      // Scroll Layout Manager
      // ================================
      _initScrollLayoutManager() {
        // Find the layout root
        this.layoutRoot = this.querySelector("[layout]");

        if (!this.layoutRoot) {
          console.error("Missing layoutRoot element");
          return;
        }

        this._initScrollComponents();
      }

      _initScrollComponents() {
        const { ScrollIdleVT, ScrollTracker, ViewTransitionManager } =
          window.ScrollTransition;

        if (!ScrollIdleVT || !ScrollTracker || !ViewTransitionManager) {
          console.error("ScrollTransition utilities not available");
          return;
        }

        // Initialize view transition manager
        this._vtManager = new ViewTransitionManager(this.layoutRoot);

        // Initialize scroll idle manager
        this._scrollIdleVT = new ScrollIdleVT((nextLayout, nextSelectId) => {
          if (!this._isRendering) {
            if (window.innerWidth < 1024) return;

            this._vtManager.setLayout(
              nextLayout,
              () => {
                if (nextSelectId != this.currentSelectionId) {
                  this._updateCards(nextSelectId);
                }
              },
              () => {
                this.currentSelectionId = nextSelectId;
              }
            );
          }
        });

        // Initialize scroll tracker with our callback
        this._scrollTracker = new ScrollTracker({ threshold: 0.3 });

        const trackIds = Object.keys(this._layoutMap);
        this._scrollTracker.add(trackIds, async (id) => {
          const nextLayout = this._layoutMap[id];
          this._scrollIdleVT.request(nextLayout, id);
        });
      }

      /*
       * Updates the cards by swapping out the media or helper content of
       * the cards based on the selection id. Content is pulled from the hidden
       * elements with the data-name attribute matching the content source.
       * @param {string} nextSelectId - The id of the selection to update
       * @param {Document} doc - The document to pull updated content from
       */
      _updateCards(nextSelectId, doc = document) {
        const nextLayout = this._layoutMap[nextSelectId];

        const contentMap = this.contentMap;
        const content = contentMap[nextSelectId];
        if (!content) return;

        const cards = this.layoutRoot.querySelectorAll("[data-card]");
        cards.forEach((card) => {
          this._updateCardContent(nextSelectId, card, doc);
        });
      }

      /*
       * Updates the content of a single card based on the selection id.
       * Content is pulled from the hidden elements with the data-name attribute
       * matching the content source.
       * @param {string} nextSelectId - The id of the selection to update
       * @param {Element} card - The card to update
       * @param {Document} doc - The document to pull updated content from
       */
      _updateCardContent(nextSelectId, card, doc = document) {
        const contentMap = this.contentMap;
        const cardName = card.dataset["card"];
        const newContent = contentMap[nextSelectId][cardName];
        const contentEls = card.querySelectorAll("[data-content]");
        if (!newContent) return;

        contentEls.forEach((contentEl) => {
          const edition = newContent.find((item) => item.type === "edition");
          const media = newContent.find((item) => item.type === "media");
          const helper = newContent.find((item) => item.type === "helper");
          contentEl.classList.add("hidden");

          // ----- EDITION (the keyboard's own images) -----
          if (contentEl.dataset["type"] === "media" && edition) {
            const mediaGalleryEl = card.querySelector(`media-gallery`);
            mediaGalleryEl.dataset["select"] = nextSelectId;
            if (this._showEditionMedia(mediaGalleryEl, edition.angle, doc)) {
              contentEl.classList.remove("hidden");
            }
          }

          // ----- MEDIA (an add-on product's gallery) -----
          if (contentEl.dataset["type"] === "media" && media) {
            const mediaGalleryEl = card.querySelector(`media-gallery`);
            mediaGalleryEl.dataset["select"] = nextSelectId;
            const srcEl = doc
              .querySelector(`template[data-name='${media.name}']`)
              ?.content.querySelector("[data-container]");
            if (srcEl) {
              contentEl.classList.remove("hidden");
              mediaGalleryEl.setImages(srcEl);
            }

            const selectionInput = this.querySelector(
              `#${nextSelectId} input:checked`
            );
            let variantId = selectionInput?.value;
            if (
              nextSelectId === "select-pcb" ||
              nextSelectId === "select-plate"
            ) {
              const variants =
                nextSelectId === "select-pcb"
                  ? this.pcbVariants
                  : this.plateVariants;
              variantId = this.getIdByOptionValue(variantId, variants);
            }
            this._showAddonMedia(nextSelectId, variantId);
          }

          // ----- HELPER -----
          if (contentEl.dataset["type"] === "helper" && helper) {
            const tpl = doc.querySelector(
              `template[data-name='${helper.name}']`
            );
            if (tpl) {
              const frag = tpl.content.cloneNode(true);
              contentEl.replaceChildren(frag);
              this.updateHelperInputs(doc);
              contentEl.classList.remove("hidden");
            }
          }
        });
      }

      _teardownScrollLayoutManager() {
        // Clean up scroll components
        this._scrollIdleVT?.destroy?.();
        this._scrollTracker?.destroy?.();
        this._scrollIdleVT = null;
        this._scrollTracker = null;
        this._vtManager = null;
      }
    }
  );
}
